import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] grid-bg flex">
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-2 h-2 bg-[#00E676] pulse" />
            <span className="mono text-[14px] tracking-[0.22em] font-bold">ALPAC/X</span>
          </div>
          <h1 className="text-5xl font-black tracking-tight leading-none mb-2">
            Trade<br/>like a<br/><span className="text-[#00E676]">terminal.</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm mt-6 mb-10 leading-relaxed">
            Stock execution, MACD strategy automation and live P&amp;L — all wired into your Alpaca account.
          </p>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            <div>
              <label className="mono text-[10px] tracking-[0.2em] text-[#71717A]">EMAIL</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1 w-full bg-[#141415] border border-[#27272A] focus:border-[#52525B] px-3 py-2.5 text-white text-sm"
                placeholder="trader@alpacx.io"
              />
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.2em] text-[#71717A]">PASSWORD</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1 w-full bg-[#141415] border border-[#27272A] focus:border-[#52525B] px-3 py-2.5 text-white text-sm"
                placeholder="••••••••"
              />
            </div>
            {error && <div className="text-[#FF3B30] mono text-xs" data-testid="login-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-white text-black py-2.5 mono text-xs tracking-[0.22em] font-bold hover:bg-[#D4D4D8] disabled:opacity-50"
            >
              {loading ? "AUTHENTICATING..." : "SIGN IN →"}
            </button>
          </form>

          <div className="mt-6 text-xs text-[#71717A]">
            No account?{" "}
            <Link to="/register" className="text-white hover:text-[#00E676]" data-testid="register-link">
              Create one →
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 border-l border-[#27272A] flex-col items-stretch justify-between p-10">
        <div className="flex items-center gap-2 mono text-[10px] tracking-[0.22em] text-[#71717A]">
          <TrendingUp size={12} /> POWERED BY ALPACA MARKETS
        </div>
        <div className="mono text-[10px] tracking-widest text-[#71717A] grid grid-cols-2 gap-6">
          <div>
            <div className="text-white text-3xl font-black mono">12·26·9</div>
            <div className="mt-1">MACD PARAMS</div>
          </div>
          <div>
            <div className="text-white text-3xl font-black mono">5.0%</div>
            <div className="mt-1">DEFAULT STOPLOSS</div>
          </div>
          <div>
            <div className="text-white text-3xl font-black mono">15<span className="text-[#71717A]">m</span></div>
            <div className="mt-1">SCAN INTERVAL</div>
          </div>
          <div>
            <div className="text-white text-3xl font-black mono">100</div>
            <div className="mt-1">TECH/AI SYMBOLS</div>
          </div>
        </div>
      </div>
    </div>
  );
}
